import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircleOutline as AcceptIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import grapeService, { Grape, GrapeAlias } from '../../../services/grapeService';

interface FormData {
  primaryName: string;
  color: 'RED' | 'WHITE' | 'PINK_ROSE' | '';
  species: string;
  aliases: GrapeAlias[];
}

interface FormErrors {
  primaryName?: string;
  color?: string;
  species?: string;
  aliases?: string;
}

interface AliasFormData {
  name: string;
  languageCode: string;
  isOfficial: boolean;
  notes: string;
}

const GrapeForm: React.FC = () => {
  const navigate = useNavigate();
  const { grapeId } = useParams<{ grapeId?: string }>();
  const isEditMode = Boolean(grapeId);
  const theme = useTheme();
  
  const [formData, setFormData] = useState<FormData>({
    primaryName: '',
    color: '',
    species: '',
    aliases: []
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [needsReview, setNeedsReview] = useState<boolean>(false);
  
  // Alias dialog state
  const [aliasDialogOpen, setAliasDialogOpen] = useState<boolean>(false);
  const [editingAliasIndex, setEditingAliasIndex] = useState<number | null>(null);
  const [aliasFormData, setAliasFormData] = useState<AliasFormData>({
    name: '',
    languageCode: '',
    isOfficial: true,
    notes: ''
  });
  const [aliasErrors, setAliasErrors] = useState<{[key: string]: string | undefined}>({});

  // Load grape data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode && grapeId) {
        setLoading(true);
        try {
          const grape = await grapeService.getGrape(grapeId);
          setFormData({
            primaryName: grape.primaryName,
            color: grape.color,
            species: grape.species || '',
            aliases: grape.aliases || []
          });
          setNeedsReview(grape.needsReview ?? false);
        } catch (error) {
          console.error('Failed to fetch grape:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isEditMode, grapeId]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error when field is changed
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  
  // Handle select changes
  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = event.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is changed
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.primaryName.trim()) {
      newErrors.primaryName = 'Primary name is required';
    }
    
    if (!formData.color) {
      newErrors.color = 'Color is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };

  // Accept (resolve needsReview without full save)
  const handleAccept = async () => {
    if (!grapeId) return;
    setAccepting(true);
    try {
      await grapeService.updateGrape(grapeId, { needsReview: false });
      setNeedsReview(false);
    } catch {
      alert('Failed to accept. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      if (isEditMode && grapeId) {
        // Update existing grape
        await grapeService.updateGrape(grapeId, {
          primaryName: formData.primaryName,
          color: formData.color as 'RED' | 'WHITE' | 'PINK_ROSE',
          species: formData.species || undefined,
          aliases: formData.aliases
        });
      } else {
        // Create new grape
        await grapeService.createGrape({
          primaryName: formData.primaryName,
          color: formData.color as 'RED' | 'WHITE' | 'PINK_ROSE',
          species: formData.species || undefined,
          aliases: formData.aliases
        });
      }
      
      // Navigate back to grape list
      navigate('/admin/grapes');
    } catch (error) {
      console.error('Failed to save grape:', error);
      alert('Failed to save grape. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Open alias dialog for adding or editing
  const openAliasDialog = (index: number | null = null) => {
    if (index !== null) {
      // Editing existing alias
      const alias = formData.aliases[index];
      setAliasFormData({
        name: alias.name,
        languageCode: alias.languageCode || '',
        isOfficial: alias.isOfficial,
        notes: alias.notes || ''
      });
      setEditingAliasIndex(index);
    } else {
      // Adding new alias
      setAliasFormData({
        name: '',
        languageCode: '',
        isOfficial: true,
        notes: ''
      });
      setEditingAliasIndex(null);
    }
    
    setAliasErrors({});
    setAliasDialogOpen(true);
  };

  // Handle alias form input changes
  const handleAliasInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setAliasFormData({
      ...aliasFormData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is changed
    if (aliasErrors[name]) {
      setAliasErrors({
        ...aliasErrors,
        [name]: undefined
      });
    }
  };

  // Validate alias form
  const validateAliasForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    
    if (!aliasFormData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setAliasErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };

  // Save alias
  const handleSaveAlias = () => {
    if (!validateAliasForm()) {
      return;
    }
    
    const newAlias: GrapeAlias = {
      name: aliasFormData.name,
      languageCode: aliasFormData.languageCode || undefined,
      isOfficial: aliasFormData.isOfficial,
      notes: aliasFormData.notes || undefined
    };
    
    const updatedAliases = [...formData.aliases];
    
    if (editingAliasIndex !== null) {
      // Update existing alias
      updatedAliases[editingAliasIndex] = newAlias;
    } else {
      // Add new alias
      updatedAliases.push(newAlias);
    }
    
    setFormData({
      ...formData,
      aliases: updatedAliases
    });
    
    setAliasDialogOpen(false);
  };

  // Delete alias
  const handleDeleteAlias = (index: number) => {
    const updatedAliases = [...formData.aliases];
    updatedAliases.splice(index, 1);
    
    setFormData({
      ...formData,
      aliases: updatedAliases
    });
  };

  // Get language name from code
  const getLanguageName = (code: string): string => {
    const languages: {[key: string]: string} = {
      'en': 'English',
      'fr': 'French',
      'it': 'Italian',
      'es': 'Spanish',
      'de': 'German',
      'pt': 'Portuguese',
      'hr': 'Croatian',
      'hu': 'Hungarian',
      'gr': 'Greek',
      'ro': 'Romanian'
    };
    
    return languages[code] || code;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Grape Variety' : 'Create Grape Variety'}
        </Typography>

        {needsReview && isEditMode && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ mb: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), color: 'text.primary', '& .MuiAlert-icon': { color: theme.palette.warning.main } }}
            action={
              <Button
                size="small"
                color="success"
                variant="outlined"
                startIcon={<AcceptIcon />}
                onClick={handleAccept}
                disabled={accepting}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {accepting ? 'Accepting…' : 'Accept'}
              </Button>
            }
          >
            <strong>Needs review</strong> — this record was auto-created during import. Review the fields and click Accept when done.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <TextField
                fullWidth
                label="Primary Name"
                name="primaryName"
                value={formData.primaryName}
                onChange={handleInputChange}
                margin="normal"
                error={Boolean(errors.primaryName)}
                helperText={errors.primaryName || 'The main international name of the grape variety'}
                required
              />
              
              <FormControl 
                fullWidth 
                margin="normal"
                error={Boolean(errors.color)}
                required
              >
                <InputLabel id="color-label">Color</InputLabel>
                <Select
                  labelId="color-label"
                  name="color"
                  value={formData.color}
                  onChange={handleSelectChange}
                  label="Color"
                >
                  <MenuItem value="RED">Red</MenuItem>
                  <MenuItem value="WHITE">White</MenuItem>
                  <MenuItem value="PINK_ROSE">Rosé</MenuItem>
                </Select>
                {errors.color && (
                  <FormHelperText>{errors.color}</FormHelperText>
                )}
              </FormControl>
              
              <TextField
                fullWidth
                label="Species"
                name="species"
                value={formData.species}
                onChange={handleInputChange}
                margin="normal"
                helperText="Grape species (e.g., Vitis vinifera)"
              />
            </Box>
            
            <Box>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Aliases</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openAliasDialog()}
                >
                  Add Alias
                </Button>
              </Box>
              
              {formData.aliases.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  No aliases added yet
                </Typography>
              ) : (
                <List>
                  {formData.aliases.map((alias, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={alias.name}
                        secondary={
                          <>
                            {alias.languageCode && (
                              <span>Language: {getLanguageName(alias.languageCode)}</span>
                            )}
                            {alias.isOfficial && (
                              <span> • Official name</span>
                            )}
                            {alias.notes && (
                              <span> • {alias.notes}</span>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => openAliasDialog(index)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" onClick={() => handleDeleteAlias(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Grape'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/admin/grapes')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
      
      {/* Alias Dialog */}
      <Dialog open={aliasDialogOpen} onClose={() => setAliasDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAliasIndex !== null ? 'Edit Alias' : 'Add Alias'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Alias Name"
            name="name"
            value={aliasFormData.name}
            onChange={handleAliasInputChange}
            margin="normal"
            error={Boolean(aliasErrors.name)}
            helperText={aliasErrors.name}
            required
          />
          
          <TextField
            fullWidth
            label="Language Code"
            name="languageCode"
            value={aliasFormData.languageCode}
            onChange={handleAliasInputChange}
            margin="normal"
            helperText="ISO 639-1 language code (2 letters, e.g., 'it' for Italian)"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={aliasFormData.isOfficial}
                onChange={handleAliasInputChange}
                name="isOfficial"
              />
            }
            label="Official name in this language"
            sx={{ mt: 1 }}
          />
          
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={aliasFormData.notes}
            onChange={handleAliasInputChange}
            margin="normal"
            multiline
            rows={2}
            helperText="Additional context or information about this alias"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAliasDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAlias} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GrapeForm;
